import {Fragment, useEffect, useState} from "react";
import {format} from "date-fns";
import {BreadCrumb, CenteredModal, Table, SkeletonTableLoading, FormInput, DropdownAction} from "~/components";
import CommonAPI from "~/api/commonApi";
import {useForm} from "react-hook-form";
import {faEdit, faEye, faTrash} from "@fortawesome/free-solid-svg-icons";
import {toast} from "react-toastify";

const breadCrumbItem = [
    { text: "Home", to: "/ai-chatbot/administrator", isActive: false },
    { text: "Page Route", to: "", isActive: true },
]

function PageRoute(){
    const [reloadTable, setReloadTable] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isAddNew, setIsAddNew] = useState(true);
    const [pageRoute, setPageRoute] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isViewMode, setIsViewMode] = useState(false);
    const [id, setId] = useState(0);
    const tableConfig = {
        columns: [
            {
                name: 'ID',
                width: '70px',
                className: 'fw-bold',
                key: 'AppRouteId'
            },
            {
                name: 'PATH',
                width: '250px',
                key: 'Path'
            },
            {
                name: 'COMPONENT',
                width: '150px',
                key: 'Component'
            },
            {
                name: 'LAYOUT',
                width: '170px',
                key: 'Layout'
            },
            {
                name: 'ROLES',
                width: '200px',
                key: 'Roles'
            },
            {
                name: 'AREA',
                width: '100px',
                key: 'Area'
            },
            {
                name: 'IS PUBLIC',
                width: '100px',
                key: "IsPublic",
                template : (row) => {
                    return (
                        <span>{row.IsPublic ? "Public Route" : "Private Route"}</span>
                    )
                }
            },
            {
                name: 'MODIFIED BY',
                width: '150px',
                key: 'ModifiedBy'
            },
            {
                name: 'MODIFIED DATE',
                width: '150px',
                key: 'ModifiedDate',
                template: (row) => {
                    return (
                        <span>{format(new Date(row.ModifiedDate), "dd-MM-yyyy")}</span>
                    )
                }
            },
            {
                name: 'ACTION',
                width: '70px',
                key: 'Action',
                headerClassName: 'text-center',
                className: 'text-center',
                template: (row) => {
                    return (
                        <div className="dropdown d-inline-block">
                            <DropdownAction dropdownItems={[
                                { text: "View Details", icon: faEye, onClick: () => handleEditPageRoute(row.AppRouteId, true) },
                                { text: "Edit", icon: faEdit, onClick: () => handleEditPageRoute(row.AppRouteId, false) },
                                {text: "Delete", icon: faTrash, onClick: () => {
                                        setId(row.AppRouteId);
                                        setShowDeleteModal(true);
                                    }}
                            ]} isVerticalIcon={false}/>
                        </div>
                    )
                }
            }
        ],
        tableKey: "TableServiceManager"
    }

    useEffect(() => {
        setLoading(true);
        CommonAPI.getAppRoute()
            .then((res) => {
                setPageRoute(res.data);
            })
            .catch((err) => {
                ;
            })
            .finally(() => {
                setLoading(false);
            })
    }, [reloadTable]);

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors }
    } = useForm();

    const fields = [
        {name: "Path", label: "Assign Path", required: true},
        {name: "Component", label: "Assign Component", required: true},
        {name: "Layout", label: "Assign Layout", required: false},
        {name: "Roles", label: "Assign Roles", required: false},
        {name: "Area", label: "Assign Area", required: false}
    ]

    const emptyValues = fields.reduce((acc, field) => {
        acc[field.name] = "";
        return acc;
    }, {});

    const handleAddNewPageRoute = () => {
        setIsViewMode(false);
        setIsAddNew(true);
        setShowModal(true);
    }

    const handleEditPageRoute = (id, viewMode) => {
        setIsViewMode(viewMode);
        CommonAPI.getAppRouteById(id)
            .then((res) => {
                reset(res.data);
                setIsAddNew(false);
                setShowModal(true);
            })
            .catch((err) => {
                ;
            })
    }

    const handleCloseModal = () => {
        setShowModal(false);
        emptyValues["IsPublic"] = false;
        reset(emptyValues);
    }

    const onSubmitFrom = (data) => {
        if(isAddNew){
            CommonAPI.appRouteInsert(data)
                .then((res) => {
                    res.data?.Success ?
                        toast.success(<span className="fs-9 fw-bold">Your page route has been added.</span>)
                        : toast.error(<span className="fs-9 fw-bold">An error occurred while adding your page route.</span>)
                })
                .catch((err) => {
                    toast.error(<span className="fs-9 fw-bold">An error occurred while adding your page route.</span>)
                })
                .finally(() => {
                    handleCloseModal();
                    setReloadTable(prev => !prev);
                })
        }
        else{
            CommonAPI.appRouteUpdate(data)
                .then((res) => {
                    res.data?.Success ?
                        toast.success(<span className="fs-9 fw-bold">Your page route has been updated.</span>)
                        : toast.error(<span className="fs-9 fw-bold">An error occurred while updating your page route.</span>)
                })
                .catch((err) => {
                    toast.error(<span className="fs-9 fw-bold">An error occurred while updating your page route.</span>)
                })
                .finally(() => {
                    handleCloseModal();
                    setReloadTable(prev => !prev);
                })
        }
    }

    const handleConfirmDelete = () => {
        CommonAPI.appRouteDelete(id)
            .then(res => {
                res.data?.Success ?
                    toast.success(<span className="fs-9 fw-bold">Your page route has been deleted.</span>)
                    : toast.error(<span
                        className="fs-9 fw-bold">An error occurred while deleting your page route.</span>)
            })
            .catch(err => {
                toast.error(<span
                    className="fs-9 fw-bold">An error occurred while deleting your page route.</span>)
                
            })
            .finally(() => {
                setShowDeleteModal(false);
                setReloadTable(prev => !prev);
            })
    }

    return (
        <Fragment>
            <BreadCrumb breadCrumbItem={breadCrumbItem}/>
            <h2 className="text-bold text-body-emphasis mb-4">Page Route Manager</h2>
            <button className="btn btn-bt-primary mb-3" type="button" onClick={handleAddNewPageRoute}>Add New Page Route</button>
            {loading ? (
                <SkeletonTableLoading columns={tableConfig.columns} rows={10} />
            ) : (
                <Table tableConfig={{...tableConfig, data : pageRoute}}/>
            )}
            <CenteredModal show={showModal}
                           title={isViewMode ? "Page Route Details" : (isAddNew ? "Add Page Route" : "Edit Page Route")}
                           onClose={handleCloseModal}
                           formId="formActionPageRoute"
                           isViewMode={isViewMode}
                           closeText={isViewMode ? "Close" : "Cancel"}
            >
                <form onSubmit={handleSubmit(onSubmitFrom)} id="formActionPageRoute">
                    <div className="mb-3">
                        {
                            fields.map((field, index) => (
                                <FormInput key = {index}
                                    showHeaderLabel={true}
                                    name = {field.name}
                                    errors={errors}
                                    required={field.required}
                                    maxLengthText=""
                                    label={field.label}
                                    disabled={isViewMode}
                                    validation={{...register(field.name, {
                                            required: field.required ? `${field.name} is required.` : false
                                        })}}
                                />
                            ))
                        }
                            <div className="form-check">
                                <input
                                    type="checkbox"
                                    id="IsPublic"
                                    className="form-check-input cursor-pointer"
                                    {...register("IsPublic")}
                                    disabled={isViewMode}
                                />
                                <label
                                    className="form-check-label fs-9 fw-semibold"
                                    htmlFor="IsPublic"
                                >
                                    Is Public Page Route?
                                </label>
                            </div>
                    </div>
                </form>
            </CenteredModal>
            <CenteredModal show={showDeleteModal}
                           onClose={()=> setShowDeleteModal(false)}
                           onConfirm={handleConfirmDelete}
                           title="Delete Page Route"
            >
                <div className="text-center">
                    <span className="fs-8-1 fw-semibold">Are you sure you want to delete this page route?</span>
                </div>
            </CenteredModal>
        </Fragment>
    )
}
export default PageRoute;